// apps/ecommerce/src/components/ai-chat/chat-container.tsx
import { useEffect, useRef } from "react";
import { ChatMessage } from "./chat-message";
import { ChatInput } from "./chat-input";
import { SessionSelector } from "./session-selector";
import { useShopifyChat } from "~/hooks/use-shopify-chat-v2";
import { useStore } from "~/stores/store-provider";

interface ChatContainerProps {
  connectionId: string;
}

export function ChatContainer({ connectionId }: ChatContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { currentShopifySessionId, createShopifyChatSession } = useStore();
  const initialLoadDoneRef = useRef(false);

  const {
    messages,
    streamingContent,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    loadMessages,
  } = useShopifyChat({ connectionId, sessionId: currentShopifySessionId });

  // Initial load: if a session is already selected on mount, load its messages
  useEffect(() => {
    if (currentShopifySessionId && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      loadMessages(currentShopifySessionId);
    }
  }, [currentShopifySessionId, loadMessages]);

  // Auto-scroll to bottom on new messages or streaming content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const handleSend = async (text: string) => {
    if (!text.trim()) return;

    // If no session exists, create one with the first message as the title
    if (!currentShopifySessionId) {
      const session = await createShopifyChatSession(connectionId, text);
      // Pass the session ID directly to avoid stale closure
      await sendMessage(text, session.session_id);
    } else {
      await sendMessage(text);
    }
  };

  return (
    <div className="flex flex-col h-full bg-base-200">
      {/* Header */}
      <div className="flex items-center justify-end bg-base-100 border-b border-base-300 px-4 py-2">
        <SessionSelector connectionId={connectionId} onSessionSelect={loadMessages} />
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-base-content/50">
            <p className="text-base">What would you like to know about your store?</p>
          </div>
        ) : null}

        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1;
          const isAssistantStreaming =
            isStreaming && isLastMessage && message.role === "assistant";

          // For streaming assistant message, show streaming content
          const displayContent = isAssistantStreaming
            ? streamingContent
            : message.content;

          return (
            <ChatMessage
              key={message._id}
              role={message.role}
              content={displayContent}
              isStreaming={isAssistantStreaming}
              tools={message.tools || []}
              connectionId={connectionId}
              artifacts={message.artifacts}
            />
          );
        })}

        {error && (
          <div className="alert alert-error">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current shrink-0 h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error.message}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <ChatInput
        onSend={handleSend}
        isLoading={isStreaming}
      />
    </div>
  );
}
